// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  JobDescription,
  JobDescriptionResponse,
  JobDescriptionState,
  JobDescriptionCreate,
  JobDescriptionUpdate,
} from "./model";
import { RequestState } from "@utils/types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { enqueueSnackbarMessage } from "@slices/commonSlice/common";
import { SnackMessage } from "@config/constant";

export const getJobDescriptions = createAsyncThunk(
  "jobDescription/getJobDescriptions",
  async ({
    jdFilter,
    searchString,
    limit,
    offset,
  }: {
    jdFilter?: string | null;
    searchString?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(AppConfig.serviceUrls.jobDescriptions, {
          params: { jdFilter, searchString, limit, offset },
        })
        .then((response) => resolve(response.data))
        .catch((error) => reject(error));
    });
  },
);

export const getJobDescriptionsById = createAsyncThunk(
  "jobDescription/getJobDescriptionsById",
  async ({ Id }: { Id: number }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .get(`${AppConfig.serviceUrls.jobDescriptions}/${Id}`)
        .then((response) => resolve(response.data))
        .catch((error) => reject(error));
    });
  },
);

export const addJobDescriptions = createAsyncThunk(
  "jobDescription/addJobDescriptions",
  async (payload: JobDescriptionCreate, { dispatch }) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(AppConfig.serviceUrls.jobDescriptions, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addJD,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addJD,
              type: "error",
            }),
          );
        });
    });
  },
);

export const updateJobDescriptions = createAsyncThunk(
  "jobDescription/updateJobDescriptions",
  async (
    { id, payload }: { id: number; payload: JobDescriptionUpdate },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .patch(`${AppConfig.serviceUrls.jobDescriptions}/${id}`, payload)
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.updateJD,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.updateJD,
              type: "error",
            }),
          );
        });
    });
  },
);

export const saveJDComment = createAsyncThunk(
  "jobDescription/saveJDComment",
  async (
    {
      jdId,
      selectedText,
      commentText,
    }: { jdId: number; selectedText: string; commentText: string },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(`${AppConfig.serviceUrls.jobDescriptions}/${jdId}/comments`, {
          selectedText,
          commentText,
        })
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addJDComment,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addJDComment,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteJDComment = createAsyncThunk(
  "jobDescription/deleteJDComment",
  async (
    { jdId, commentId }: { jdId: number; commentId: string },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .delete(
          `${AppConfig.serviceUrls.jobDescriptions}/${jdId}/comments/${commentId}`,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.deleteJDComment,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.deleteJDComment,
              type: "error",
            }),
          );
        });
    });
  },
);

export const saveJDCommentReply = createAsyncThunk(
  "jobDescription/saveJDCommentReply",
  async (
    {
      jdId,
      commentId,
      replyText,
    }: { jdId: number; commentId: string; replyText: string },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .post(
          `${AppConfig.serviceUrls.jobDescriptions}/${jdId}/comments/${commentId}/replies`,
          { commentText: replyText },
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.addJDComment,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.addJDComment,
              type: "error",
            }),
          );
        });
    });
  },
);

export const deleteJDCommentReply = createAsyncThunk(
  "jobDescription/deleteJDCommentReply",
  async (
    {
      jdId,
      commentId,
      replyId,
    }: { jdId: number; commentId: string; replyId: string },
    { dispatch },
  ) => {
    return new Promise<any>((resolve, reject) => {
      APIService.getInstance()
        .delete(
          `${AppConfig.serviceUrls.jobDescriptions}/${jdId}/comments/${commentId}/replies/${replyId}`,
        )
        .then((response) => {
          resolve(response.data);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.success.deleteJDComment,
              type: "success",
            }),
          );
        })
        .catch((error) => {
          reject(error);
          dispatch(
            enqueueSnackbarMessage({
              message: SnackMessage.error.deleteJDComment,
              type: "error",
            }),
          );
        });
    });
  },
);

const initialState: JobDescriptionState = {
  jobDescriptionResponse: {
    jobDescriptions: [],
    totalCount: 0,
  },
  state: RequestState.IDLE,
};

const jobDescriptionSlice = createSlice({
  name: "jobDescription",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getJobDescriptions.pending, (state) => {
      state.state = RequestState.LOADING;
      state.stateMessage = "Loading job descriptions...";
    });
    builder.addCase(
      getJobDescriptions.fulfilled,
      (state, action: PayloadAction<JobDescriptionResponse>) => {
        state.jobDescriptionResponse = action.payload;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getJobDescriptions.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(getJobDescriptionsById.pending, (state) => {
      state.state = RequestState.LOADING;
      state.stateMessage = "Loading job description...";
    });
    builder.addCase(
      getJobDescriptionsById.fulfilled,
      (state, action: PayloadAction<JobDescription>) => {
        const exists = state.jobDescriptionResponse.jobDescriptions.some(
          (jd) => jd.id === action.payload.id,
        );
        if (exists) {
          state.jobDescriptionResponse.jobDescriptions =
            state.jobDescriptionResponse.jobDescriptions.map((jd) =>
              jd.id === action.payload.id ? action.payload : jd,
            );
        } else {
          state.jobDescriptionResponse.jobDescriptions = [
            ...state.jobDescriptionResponse.jobDescriptions,
            action.payload,
          ];
        }
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(getJobDescriptionsById.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(addJobDescriptions.pending, (state) => {
      state.state = RequestState.LOADING;
    });
    builder.addCase(
      addJobDescriptions.fulfilled,
      (state, action: PayloadAction<JobDescription>) => {
        state.jobDescriptionResponse.jobDescriptions = [
          ...state.jobDescriptionResponse.jobDescriptions.filter(
            (jd) => jd.id !== action.payload.id,
          ),
          action.payload,
        ];
        state.jobDescriptionResponse.totalCount += 1;
        state.state = RequestState.SUCCEEDED;
      },
    );
    builder.addCase(addJobDescriptions.rejected, (state) => {
      state.state = RequestState.FAILED;
    });

    builder.addCase(updateJobDescriptions.pending, (state) => {
      state.state = RequestState.LOADING;
      state.stateMessage = "Updating job description...";
    });
    builder.addCase(
      updateJobDescriptions.fulfilled,
      (state, action: PayloadAction<JobDescription>) => {
        state.jobDescriptionResponse.jobDescriptions =
          state.jobDescriptionResponse.jobDescriptions.map((jd) =>
            jd.id === action.payload.id ? action.payload : jd,
          );
        state.state = RequestState.SUCCEEDED;
        state.stateMessage = "Job description updated successfully";
      },
    );
    builder.addCase(updateJobDescriptions.rejected, (state) => {
      state.state = RequestState.FAILED;
      state.stateMessage = "Failed to update job description";
    });

    builder.addCase(saveJDCommentReply.pending, (state) => {
      state.state = RequestState.LOADING;
      state.stateMessage = "Saving reply...";
    });
    builder.addCase(
      saveJDCommentReply.fulfilled,
      (state, action: PayloadAction<JobDescription>) => {
        state.jobDescriptionResponse.jobDescriptions =
          state.jobDescriptionResponse.jobDescriptions.map((jd) =>
            jd.id === action.payload.id ? action.payload : jd,
          );
        state.state = RequestState.SUCCEEDED;
        state.stateMessage = "Reply saved successfully";
      },
    );
    builder.addCase(saveJDCommentReply.rejected, (state) => {
      state.state = RequestState.FAILED;
      state.stateMessage = "Failed to save reply";
    });

    builder.addCase(deleteJDCommentReply.pending, (state) => {
      state.state = RequestState.LOADING;
      state.stateMessage = "Deleting reply...";
    });
    builder.addCase(
      deleteJDCommentReply.fulfilled,
      (state, action: PayloadAction<JobDescription>) => {
        state.jobDescriptionResponse.jobDescriptions =
          state.jobDescriptionResponse.jobDescriptions.map((jd) =>
            jd.id === action.payload.id ? action.payload : jd,
          );
        state.state = RequestState.SUCCEEDED;
        state.stateMessage = "Reply deleted successfully";
      },
    );
    builder.addCase(deleteJDCommentReply.rejected, (state) => {
      state.state = RequestState.FAILED;
      state.stateMessage = "Failed to delete reply";
    });
  },
});

export default jobDescriptionSlice.reducer;
